/**
 * AI brand-brief generator dialog (#230).
 *
 * Mirror of `ThemeGeneratorDialog.tsx` (#192). Opens from the
 * BrandBriefSection editor's "Generate with AI" button. A brand-pitch
 * textarea + the shared `BudgetBanner` + a submit button. On success the
 * proposed `brandJson` is handed back to the parent via `onApply` so the
 * BrandBriefSection can populate its react-hook-form values — the dialog
 * does NOT save the brief itself. The operator still has to click
 * "Save brand brief" in the editor to PATCH the site.
 *
 * Error handling mirrors the AI Studio surface:
 *   - 429 `AI_RATE_LIMITED` / `AI_BUDGET_EXCEEDED` → error toast (the
 *     banner above the prompt also surfaces the budget state).
 *   - 422 `AI_INVALID_PLAN` → error toast with the planner's diagnostic
 *     message.
 *   - Other errors → generic error toast.
 *
 * MUI only — no Tailwind, CSS modules, or raw CSS per
 * `standards/react-patterns.md`.
 *
 * `data-testid` attributes for QA hooks:
 *   - `brand-brief-ai-dialog`
 *   - `brand-brief-ai-brief`
 *   - `brand-brief-ai-submit`
 *   - `brand-brief-ai-cancel`
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import type { BrandJsonV1 } from "@ottopilot/shared";
import { brandJsonV1 } from "@ottopilot/shared";
import { useGenerateBrandBrief } from "../../hooks/useAiStudio";
import { useToast } from "../../components/ToastProvider";
import { BudgetBanner } from "../ai/BudgetBanner";
import type { ApiClientError } from "../../lib/api";

/** Mirrors the server-side cap on `aiGenerateBrandBriefSchema.prompt`. */
const PROMPT_MAX_LEN = 8_000;

export type BrandBriefGeneratorDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The Site id whose brand brief is being generated. */
  siteId: string;
  /**
   * Called with the validated `BrandJsonV1` proposal on success. The
   * parent BrandBriefSection uses this to seed its react-hook-form values
   * — the operator confirms by clicking "Save brand brief" in the editor.
   */
  onApply: (brandJson: BrandJsonV1) => void;
};

export function BrandBriefGeneratorDialog({
  open,
  onClose,
  siteId,
  onApply,
}: BrandBriefGeneratorDialogProps): ReactElement {
  const toast = useToast();
  const [prompt, setPrompt] = useState("");
  const generate = useGenerateBrandBrief(siteId);

  const trimmedLen = prompt.trim().length;
  const canSubmit = trimmedLen > 0 && trimmedLen <= PROMPT_MAX_LEN && !generate.isPending;

  const handleClose = () => {
    if (generate.isPending) return;
    setPrompt("");
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    generate.mutate(
      { prompt: prompt.trim() },
      {
        onSuccess: (data) => {
          // The API has already validated against `brandJsonV1`, but we
          // re-narrow on the client too so `onApply` receives the
          // strict-typed shape rather than the wire-level `unknown`.
          const parsed = brandJsonV1.safeParse(data.brandJson);
          if (!parsed.success) {
            toast.showError(
              "AI returned a brand brief that failed client-side validation. Please try again.",
            );
            return;
          }
          toast.showSuccess("Brand brief applied. Review and click Save brand brief to persist.");
          onApply(parsed.data);
          setPrompt("");
          onClose();
        },
        onError: (err: ApiClientError) => {
          if (err.code === "AI_RATE_LIMITED") {
            toast.showError("Too many AI calls this minute. Please wait and try again.");
            return;
          }
          if (err.code === "AI_BUDGET_EXCEEDED") {
            toast.showError(
              "Monthly AI budget exceeded. Start an override window from the banner above.",
            );
            return;
          }
          if (err.code === "AI_INVALID_PLAN") {
            toast.showError(
              "AI returned a brand brief that failed validation. Try a more specific pitch.",
            );
            return;
          }
          if (err.code === "SITE_NOT_FOUND") {
            toast.showError("Site not found. Refresh and try again.");
            return;
          }
          toast.showError(err.message || "Could not generate brand brief.");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      data-testid="brand-brief-ai-dialog"
    >
      <DialogTitle>Generate brand brief with AI</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <BudgetBanner />
          <DialogContentText>
            Describe the brand in your own words — what you sell, who you sell it to, the feel you
            want. The AI will propose a tagline, tone, audience, and voice notes. The proposal
            populates the editor; nothing is saved until you click <b>Save brand brief</b>.
          </DialogContentText>
          <Box>
            <TextField
              label="Brand pitch"
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              autoFocus
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                "e.g. We sell hand-poured candles to people in their 30s who " +
                "want their flat to feel like a small luxury hotel. Tone is calm and considered."
              }
              error={trimmedLen > PROMPT_MAX_LEN}
              helperText={
                trimmedLen > PROMPT_MAX_LEN
                  ? `Pitch is ${String(trimmedLen)} characters — please trim to ${String(PROMPT_MAX_LEN)}.`
                  : `${String(trimmedLen)} / ${String(PROMPT_MAX_LEN)}`
              }
              inputProps={{ "data-testid": "brand-brief-ai-brief" }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={generate.isPending}
          data-testid="brand-brief-ai-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="brand-brief-ai-submit"
        >
          {generate.isPending ? "Generating…" : "Generate brand brief"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
